import { useEffect, useMemo } from 'react';
import { useQuery } from '@apollo/client';
import {
  CardCollapsable,
  FlexContainer,
  Label,
  Spinner,
} from '@facephi/ui-react';
import { useTranslation } from 'react-i18next';
import { useBehaviour } from '../../providers/BehaviourProvider';
import { SessionOperation } from '../../state/model/operation';
import { getOperation } from '../../state/queries';
import { CardContainer, CellContainer, RowContainerGrid } from './Styles';
type Props = {
  operationId: string;
  onExceptionError?: () => void;
  onEmptyData?: () => void;
};
export const OperationDetail = ({
  operationId,
  onEmptyData,
  onExceptionError,
}: Props) => {
  const { t } = useTranslation();
  const { companyId } = useBehaviour();
  const {
    loading: isLoading,
    error: isError,
    data: { session } = {},
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
  } = useQuery<{ session: SessionOperation }>(getOperation as any, {
    variables: {
      operationId,
      companyId,
    },
  });

  const hasSession = Object.keys(session || {}).length;

  const { alerts, ...rest } = useMemo(() => {
    if (!isLoading && hasSession && session) {
      const {
        operationSystem: operatingSystem,
        screen,
        ...device
      } = session.devices[0];
      return {
        alerts: session.alerts,
        session: {
          sessionId: session.sessionId,
          company: session.company,
          userId: session.userId,
          registerDate: session.registerDate,
          score: session.score,
          source: session.source,
        },
        networks: session.networks.map((network) => ({
          ...network,
          geolocation: `${network.geolocation.country}, ${network.geolocation.region}, ${network.geolocation.city}`,
        })),
        device,
        operatingSystem,
        screen: {
          ...screen,
          resolution: screen.resolution.join(', '),
        },
      };
    } else if (!isLoading && !hasSession) {
      onEmptyData?.();
    }
    return {};
  }, [session, isLoading]);

  useEffect(() => {
    isError && onExceptionError?.();
  }, [isError]);

  return (
    <FlexContainer flexDirection="column" rowGap="1" style={{ height: '100%' }}>
      {isLoading ? (
        <Spinner insideComponent />
      ) : hasSession ? (
        <>
          <CardCollapsable
            withShadow
            title={t('alerts')}
            isOpen={true}
            iconLeft={alerts?.length ? 'XCircle' : 'CheckCircle'}
            iconLeftColor={alerts?.length ? 'error400' : 'success400'}
            iconLeftWeight="fill"
          >
            {alerts?.length ? (
              <RowContainerGrid gridTemplateColumns="repeat(3, 1fr)">
                {alerts.map((alert) =>
                  Object.entries(alert).map(([key, value], index) => (
                    <CellContainer key={index}>
                      <Label>{t(key)}</Label>
                      <Label semibold color="error400" fontSize="16">
                        {value}
                      </Label>
                    </CellContainer>
                  ))
                )}
              </RowContainerGrid>
            ) : (
              <div style={{ textAlign: 'center' }}>
                <Label fontSize="24">{t('No alerts')}</Label>
              </div>
            )}
          </CardCollapsable>
          {Object.entries(rest || {})?.map(([name, option], index) => (
            <CardCollapsable withShadow title={t(name)} key={index}>
              <CardContainer>
                {(!Array.isArray(option) ? [option] : option).map(
                  (item, count) => (
                    <FlexContainer wrap="wrap" key={name + count}>
                      {Object.entries(item).map(([key, value], num) => (
                        <CellContainer
                          key={name + count + num}
                          flex="0 0 calc(100%/3)"
                          alignItems="flex-start"
                        >
                          <Label>{t(key)}</Label>
                          <Label semibold>
                            {(value === null ? '-' : value) as string}
                          </Label>
                        </CellContainer>
                      ))}
                    </FlexContainer>
                  )
                )}
              </CardContainer>
            </CardCollapsable>
          ))}
        </>
      ) : null}
    </FlexContainer>
  );
};
